import type EsriCamera from "@arcgis/core/Camera";
import type EsriPoint from "@arcgis/core/geometry/Point";
import type Graphic from "@arcgis/core/Graphic";
import type Layer from "@arcgis/core/layers/Layer";
import type SceneLayer from "@arcgis/core/layers/SceneLayer";
import type SceneView from "@arcgis/core/views/SceneView";
import { blue, grey, LocationOpaque } from "@faro-lotv/flat-ui";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import { MathUtils, PerspectiveCamera, Plane, Vector3 } from "three";
import { toMapLocal, WorldMapPose } from "./world-map-alignment";
import { toGeoCamera } from "./world-map-camera";
import { clippingBoxFootprint } from "./world-map-clipping";
import { useWorldMapStore } from "./world-map-store";
import { GeoPosition, mapLocalToGeo, WorldMapStyle } from "./world-map-tiles";

/** The ArcGIS basemaps the Dashboard uses for each style */
const BASEMAP_BY_STYLE: Record<WorldMapStyle, string> = {
  [WorldMapStyle.map]: "topo-3d",
  [WorldMapStyle.satellite]: "hybrid",
};

// Marker as in the Dashboard (world-map-constants.ts)
const MARKER_HEIGHT_PX = 24;
const MARKER_SVG_COLOR = "#ff4500";
const MARKER_LIFT_SCREEN_PX = 40;
const MARKER_LIFT_MIN_M = 8;
const MARKER_LIFT_MAX_M = 200;
const MARKER_CALLOUT_PX = 1.5;
const MARKER_LIFT_MIN_TILT_DEG = 15;
/** The marker appears once the data is smaller than this on screen, and hides above MARKER_HIDE_PX */
const MARKER_SHOW_PX = 20;
const MARKER_HIDE_PX = 30;

type MapScene = {
  view: SceneView;
  Camera: typeof EsriCamera;
  Point: typeof EsriPoint;
  /** Pin at the project, for when the data is too far away to be seen */
  marker: Graphic;
  /** Height of the ArcGIS terrain at the project, which maps to the height of the map in the scene */
  groundElevation: number;
};

type WorldMapSceneViewProps = {
  /** The project coordinates */
  position: GeoPosition;
  /** The placement of the map in the scene */
  pose: WorldMapPose;
  /** The height of the map in the scene */
  height: number;
  /** Center of the data in the scene, where the marker is placed */
  center: Vector3;
  /** Radius of the data, to tell when it is too small on screen */
  radius: number;
  /** The clipping planes of the scene, the buildings inside the clipping box are hidden */
  clippingPlanes?: Plane[];
};

/**
 * Shows the Dashboard's ArcGIS 3D map behind the scene, with its camera following the viewer camera.
 * The viewer renders on a transparent background meanwhile, so the map shows through empty areas.
 *
 * @returns Nothing in the scene, the map is a separate canvas behind it
 */
export function WorldMapSceneView({
  position,
  pose,
  height,
  center,
  radius,
  clippingPlanes,
}: WorldMapSceneViewProps): null {
  const style = useWorldMapStore((state) => state.style);
  const footprint = useMemo(
    () =>
      clippingPlanes && clippingBoxFootprint(clippingPlanes, position, pose),
    [clippingPlanes, position, pose],
  );
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);
  const invalidate = useThree((state) => state.invalidate);
  const [mapScene, setMapScene] = useState<MapScene>();
  const [buildings, setBuildings] = useState<SceneLayer>();
  const isMarkerLifted = useRef(false);

  useEffect(() => {
    const parent = gl.domElement.parentElement;
    if (!parent) return;
    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "absolute",
      inset: "0",
      zIndex: "-1",
      pointerEvents: "none",
    });
    // Keeps the map exactly behind the canvas, and above the backgrounds of the page
    const previousStyle = {
      position: parent.style.position,
      isolation: parent.style.isolation,
    };
    parent.style.position = "relative";
    parent.style.isolation = "isolate";
    parent.prepend(container);

    let view: SceneView | undefined;
    let cancelled = false;
    void createMapScene(container, position)
      .then((created) => {
        ({ view } = created);
        if (cancelled) view.destroy();
        else setMapScene(created);
      })
      // The map is optional context, the viewer keeps working without it
      .catch(() => undefined);
    return () => {
      cancelled = true;
      view?.destroy();
      container.remove();
      Object.assign(parent.style, previousStyle);
      setMapScene(undefined);
    };
  }, [gl, position]);

  useEffect(() => {
    const map = mapScene?.view.map;
    if (!map) return;
    map.basemap = BASEMAP_BY_STYLE[style];
    let cancelled = false;
    void map.basemap
      ?.load()
      .then(async (basemap) => {
        const layer = basemap.referenceLayers.find(isBuildingsLayer);
        if (!layer || !isBuildingsLayer(layer)) return;
        await styleBuildings(layer);
        if (!cancelled) setBuildings(layer);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      setBuildings(undefined);
    };
  }, [mapScene, style]);

  // Hides the buildings inside the clipping box, so that they do not cover the data
  useEffect(() => {
    if (!buildings) return;
    buildings.filter = footprint
      ? {
          geometries: [
            { rings: [footprint], spatialReference: { wkid: 4326 } },
          ],
          spatialRelationship: "disjoint",
        }
      : null;
  }, [buildings, footprint]);

  useEffect(() => {
    if (!mapScene) return;
    const { background } = scene;
    const clearAlpha = gl.getClearAlpha();
    scene.background = null;
    gl.setClearAlpha(0);
    invalidate();
    return () => {
      scene.background = background;
      gl.setClearAlpha(clearAlpha);
      invalidate();
    };
  }, [mapScene, scene, gl, invalidate]);

  useEffect(() => {
    if (!mapScene) return;
    const [east, z] = toMapLocal(pose, [center.x, center.z]);
    const { lat, lng } = mapLocalToGeo(position, east, -z);
    mapScene.marker.geometry = new mapScene.Point({
      longitude: lng,
      latitude: lat,
    });
  }, [mapScene, position, pose, center]);

  useFrame(({ camera, size }) => {
    if (!mapScene || !(camera instanceof PerspectiveCamera)) return;
    const { longitude, latitude, z, ...orientation } = toGeoCamera(
      camera,
      position,
      pose,
      height,
      mapScene.groundElevation,
    );
    mapScene.view.camera = new mapScene.Camera({
      position: { longitude, latitude, z },
      ...orientation,
    });

    const { marker } = mapScene;
    const radiusPx =
      (radius * size.height) /
      (2 *
        camera.position.distanceTo(center) *
        Math.tan((camera.fov * MathUtils.DEG2RAD) / 2));
    const showMarker =
      radiusPx < (marker.visible ? MARKER_HIDE_PX : MARKER_SHOW_PX);
    if (marker.visible !== showMarker) marker.visible = showMarker;
    // Lifted with a leader line when tilted, so that buildings do not hide the pin
    const isLifted = orientation.tilt >= MARKER_LIFT_MIN_TILT_DEG;
    if (isMarkerLifted.current !== isLifted) {
      isMarkerLifted.current = isLifted;
      marker.symbol = markerSymbol(isLifted);
    }
  });

  return null;
}

/**
 * @param isLifted True to raise the pin above the ground with a leader line
 * @returns The project marker symbol of the Dashboard's 3D map
 */
function markerSymbol(
  isLifted: boolean,
): __esri.PointSymbol3DProperties & { type: "point-3d" } {
  const color = blue[500];
  return {
    type: "point-3d",
    symbolLayers: [
      {
        type: "icon",
        resource: {
          href: LocationOpaque.replace(
            encodeURIComponent(MARKER_SVG_COLOR),
            encodeURIComponent(color),
          ),
        },
        size: MARKER_HEIGHT_PX,
        anchor: "bottom",
      },
    ],
    ...(isLifted && {
      verticalOffset: {
        screenLength: MARKER_LIFT_SCREEN_PX,
        minWorldLength: MARKER_LIFT_MIN_M,
        maxWorldLength: MARKER_LIFT_MAX_M,
      },
      callout: {
        type: "line",
        size: MARKER_CALLOUT_PX,
        color,
        border: { color: grey[0] },
      },
    }),
  };
}

/**
 * @param layer A layer of the basemap
 * @returns True for the 3D buildings of the `topo-3d` basemap
 */
function isBuildingsLayer(layer: Layer): layer is SceneLayer {
  return layer.type === "scene" && layer.title === "Buildings";
}

/**
 * Recolors the 3D buildings to a grey with thin darker edges, as the Dashboard does.
 *
 * @param buildings The buildings layer of the `topo-3d` basemap
 */
async function styleBuildings(buildings: SceneLayer): Promise<void> {
  const [
    { default: SimpleRenderer },
    { default: MeshSymbol3D },
    { default: FillSymbol3DLayer },
    { default: SolidEdges3D },
  ] = await Promise.all([
    import("@arcgis/core/renderers/SimpleRenderer"),
    import("@arcgis/core/symbols/MeshSymbol3D"),
    import("@arcgis/core/symbols/FillSymbol3DLayer"),
    import("@arcgis/core/symbols/edges/SolidEdges3D"),
  ]);
  buildings.renderer = new SimpleRenderer({
    symbol: new MeshSymbol3D({
      symbolLayers: [
        new FillSymbol3DLayer({
          material: { color: grey[200], colorMixMode: "replace" },
          edges: new SolidEdges3D({ color: grey[300], size: 1 }),
        }),
      ],
    }),
  });
}

/**
 * @param container The element to show the map in
 * @param position The project coordinates
 * @returns An ArcGIS 3D view set up like the Dashboard map
 */
async function createMapScene(
  container: HTMLDivElement,
  position: GeoPosition,
): Promise<MapScene> {
  const [
    { default: EsriMap },
    { default: EsriSceneView },
    { default: Camera },
    { default: Point },
    { default: Graphic },
    { default: GraphicsLayer },
  ] = await Promise.all([
    import("@arcgis/core/Map"),
    import("@arcgis/core/views/SceneView"),
    import("@arcgis/core/Camera"),
    import("@arcgis/core/geometry/Point"),
    import("@arcgis/core/Graphic"),
    import("@arcgis/core/layers/GraphicsLayer"),
    import("@arcgis/core/assets/esri/themes/light/main.css"),
  ]);
  const marker = new Graphic({ symbol: markerSymbol(false), visible: false });
  const map = new EsriMap({
    ground: "world-elevation",
    layers: [
      new GraphicsLayer({
        graphics: [marker],
        elevationInfo: { mode: "relative-to-scene" },
      }),
    ],
  });
  const view = new EsriSceneView({
    container,
    map,
    ui: { components: ["attribution"] },
    // Lit from the camera like the Dashboard, so the map never falls into night
    environment: { lighting: { type: "virtual" } },
  });
  await map.ground.load();
  // The viewer camera may go below the ArcGIS terrain, e.g. in a basement
  map.ground.navigationConstraint = { type: "none" };
  const { geometry } = await map.ground.queryElevation(
    new Point({ longitude: position.lng, latitude: position.lat }),
  );
  const groundElevation = geometry.type === "point" ? (geometry.z ?? 0) : 0;
  return { view, Camera, Point, marker, groundElevation };
}
